/**
 * Silent JWT refresh manager + axios interceptors.
 *
 * Behaviour:
 *  • Pro-active: when an outgoing request finds the access token < 5 min from
 *    expiry, the request waits for an in-flight refresh before going out.
 *  • Reactive: when a response comes back 401 with detail mentioning expired
 *    or invalid token, we trigger a single refresh and retry the original
 *    request once with the new access token.
 *  • Single-flight: concurrent calls share one in-flight refresh promise; all
 *    queued requests receive the same fresh token before they fire.
 *  • Graceful: if the refresh itself fails (bad/expired refresh token), we
 *    clear all auth state, show a friendly toast, and route to /login.
 *  • The login flow & UI are unchanged — we just additionally store the
 *    refresh token alongside the existing `token` localStorage key.
 */
import axios from "axios";
import { toast } from "sonner";

const TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refresh_token";
const TECH_TOKEN_KEY = "tech_token";
const TECH_REFRESH_TOKEN_KEY = "tech_refresh_token";
// Explicit "which session is active" marker. Set at login time and read by
// `getActiveSession()` so we never decide auth based on URL pathname (which
// races with React Router redirects and led to a real bug where a tech user
// briefly on /dashboard would have the interceptor reach for owner keys).
//
// Values: "owner" | "tech"
const AUTH_KIND_KEY = "auth_kind";

// How close to expiry should we pro-actively refresh? 5 minutes per spec.
const PROACTIVE_REFRESH_WINDOW_MS = 5 * 60 * 1000;

// Foreground-resume refresh window. WIDER than the proactive request window
// because when the PWA returns from background we want to refresh
// aggressively — better one extra refresh call than a user-visible 401
// flicker on the first request after re-opening the app. Covers the case
// where the app was backgrounded for 50+ minutes and the access token is
// 5-10 min from expiry: we refresh on resume so the first network call
// after returning never hits a stale token.
const FOREGROUND_REFRESH_WINDOW_MS = 15 * 60 * 1000;

// The URL prefix for refresh endpoint detection (and the call itself).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_PREFIX = `${BACKEND_URL}/api`;
const REFRESH_PATH = "/api/auth/refresh";

// Single in-flight refresh promise. While set, every other request that
// detects an expired token will await this same promise rather than firing
// its own /auth/refresh call (single-flight lock).
let inflightRefresh = null;

// Set by AuthContext (or App.js) so the interceptor can soft-logout without
// importing react-router. This avoids a hard window.location reload that
// would clobber any unsaved state in unrelated components.
let onSessionEnded = null;

export function setOnSessionEnded(fn) {
  onSessionEnded = fn;
}

// ─── Token storage helpers ──────────────────────────────────────────────

export const tokenStorage = {
  getAccess: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setAccess: (t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      localStorage.setItem(AUTH_KIND_KEY, "owner");
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  },
  setRefresh: (t) => {
    if (t) localStorage.setItem(REFRESH_TOKEN_KEY, t);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
  setPair: ({ token, refresh_token }) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    if (token || refresh_token) localStorage.setItem(AUTH_KIND_KEY, "owner");
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TECH_TOKEN_KEY);
    localStorage.removeItem(TECH_REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_KIND_KEY);
    localStorage.removeItem("user");
    localStorage.removeItem("tech_user");
  },
};

// Tech sessions reuse the same refresh endpoint — minor sugar wrapper:
export const techTokenStorage = {
  getAccess: () => localStorage.getItem(TECH_TOKEN_KEY),
  getRefresh: () => localStorage.getItem(TECH_REFRESH_TOKEN_KEY),
  setPair: ({ token, refresh_token }) => {
    if (token) localStorage.setItem(TECH_TOKEN_KEY, token);
    if (refresh_token) localStorage.setItem(TECH_REFRESH_TOKEN_KEY, refresh_token);
    if (token || refresh_token) localStorage.setItem(AUTH_KIND_KEY, "tech");
  },
};

// ─── JWT decode helper (no external deps) ──────────────────────────────

function base64UrlDecode(str) {
  // JWT uses URL-safe base64 without padding. Restore padding then decode.
  const pad = (4 - (str.length % 4)) % 4;
  const b64 = str.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(pad);
  try {
    return atob(b64);
  } catch {
    return null;
  }
}

export function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  const decoded = base64UrlDecode(parts[1]);
  if (!decoded) return null;
  try {
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Returns the JWT's exp claim as a JS timestamp (ms), or null when missing.
 */
export function getTokenExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return null;
  return payload.exp * 1000;
}

/** True if the token expires inside `windowMs` from now (default 5 min). */
export function isExpiringSoon(token, windowMs = PROACTIVE_REFRESH_WINDOW_MS) {
  const exp = getTokenExpiryMs(token);
  if (exp == null) return false; // unknown shape — let the server decide
  return exp - Date.now() <= windowMs;
}

/** True if the token is already past expiry. */
export function isExpired(token) {
  const exp = getTokenExpiryMs(token);
  if (exp == null) return false;
  return Date.now() >= exp;
}

/**
 * Determine which token pair to use for the current session.
 *
 * **Storage key selection is based on the user's role, NOT the URL path.**
 *
 * Resolution order (deterministic, no URL involved):
 *   1. Explicit `auth_kind` marker in localStorage (set at login by the
 *      tokenStorage / techTokenStorage helpers).
 *   2. Decoded JWT `role` / `type` claim if either pair has tokens whose
 *      payload says `role: "technician"`.
 *   3. Fallback inference from "which token pair currently exists" — covers
 *      legacy localStorage from before the marker was introduced and any
 *      caller that bypassed the storage helpers.
 *   4. Default to owner.
 *
 * Never references `window.location.pathname` — that races with redirects
 * and historically caused tech users to briefly hit owner storage keys.
 */
function getActiveSession() {
  // 1. Explicit marker.
  let kind = null;
  try {
    const v = localStorage.getItem(AUTH_KIND_KEY);
    if (v === "tech" || v === "owner") kind = v;
  } catch { /* localStorage may throw in private mode — treat as missing */ }

  // 2. JWT role claim — checked even when marker exists, so a malformed
  //    marker can't trump the actual token payload.
  if (!kind) {
    const techAccess = localStorage.getItem(TECH_TOKEN_KEY);
    const ownerAccess = localStorage.getItem(TOKEN_KEY);
    const techPayload = decodeJwtPayload(techAccess);
    const ownerPayload = decodeJwtPayload(ownerAccess);
    const techIsTech = techPayload?.role === "technician" || techPayload?.tech_id != null;
    const ownerIsOwner = ownerPayload && ownerPayload.role !== "technician";
    if (techIsTech && !ownerIsOwner) kind = "tech";
    else if (ownerIsOwner && !techIsTech) kind = "owner";
  }

  // 3. Fallback: whichever pair has tokens.
  if (!kind) {
    const hasTech = !!(localStorage.getItem(TECH_TOKEN_KEY) || localStorage.getItem(TECH_REFRESH_TOKEN_KEY));
    const hasOwner = !!(localStorage.getItem(TOKEN_KEY) || localStorage.getItem(REFRESH_TOKEN_KEY));
    if (hasTech && !hasOwner) kind = "tech";
    else if (hasOwner && !hasTech) kind = "owner";
    else kind = "owner"; // 4. final default
  }

  if (kind === "tech") {
    return {
      kind: "tech",
      access: techTokenStorage.getAccess(),
      refresh: techTokenStorage.getRefresh(),
      setPair: techTokenStorage.setPair,
      loginPath: "/tech/login",
    };
  }
  return {
    kind: "owner",
    access: tokenStorage.getAccess(),
    refresh: tokenStorage.getRefresh(),
    setPair: tokenStorage.setPair,
    loginPath: "/login",
  };
}

// ─── Refresh logic ──────────────────────────────────────────────────────

/**
 * Perform a single refresh call. Returns { token, refresh_token } on success
 * and throws on any failure (caller MUST handle by clearing auth state).
 */
async function performRefresh() {
  const session = getActiveSession();
  if (!session.refresh) throw new Error("no_refresh_token");
  // Use a bare axios instance (no interceptors) to avoid recursion.
  const bare = axios.create();
  const res = await bare.post(
    `${API_PREFIX}/auth/refresh`,
    { refresh_token: session.refresh },
    { headers: { "Content-Type": "application/json" } }
  );
  if (!res.data?.token || !res.data?.refresh_token) {
    throw new Error("malformed_refresh_response");
  }
  session.setPair(res.data);
  return res.data;
}

/**
 * Ensures only one refresh call is in flight. All other callers awaiting
 * refresh receive the same Promise — including its rejection.
 */
export function refreshAccessToken() {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      return await performRefresh();
    } finally {
      // Whether success or fail, release the lock so future requests can
      // attempt fresh refreshes (or fail again predictably).
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

/**
 * Hard-logout: clear local state, show friendly toast, and route to /login.
 * Wrapped in a guard so multiple concurrent failures don't trigger multiple
 * redirects (and only show one toast).
 */
let sessionEndedFired = false;
export function endSession(reason = "expired") {
  if (sessionEndedFired) return;
  sessionEndedFired = true;
  tokenStorage.clear();
  try {
    toast.error("Your session ended", {
      description: "Please log in again to continue.",
      duration: 5000,
    });
  } catch {
    // sonner not yet mounted — ignore.
  }
  if (typeof onSessionEnded === "function") {
    try { onSessionEnded(reason); } catch { /* swallow */ }
  } else if (typeof window !== "undefined") {
    // Fallback: hard navigation. Defer slightly so the toast renders.
    // Decide the redirect destination from the role marker (set at login),
    // NOT from window.location.pathname — same rule as getActiveSession().
    // Pathname is only consulted as a last resort when no marker AND no
    // tokens exist, just so we don't loop on the wrong /login route.
    let next = "/login";
    try {
      const kindMarker = localStorage.getItem(AUTH_KIND_KEY);
      if (kindMarker === "tech") next = "/tech/login";
      else if (kindMarker === "owner") next = "/login";
      else if (window.location.pathname.startsWith("/tech")) next = "/tech/login";
    } catch { /* default to /login */ }
    setTimeout(() => {
      if (window.location.pathname !== next) window.location.href = next;
    }, 250);
  }
  // Re-arm the guard after the redirect window so subsequent fresh sessions
  // can also be ended cleanly later.
  setTimeout(() => { sessionEndedFired = false; }, 5000);
}

// Helpers used by the interceptors
function isAuthEndpoint(url) {
  if (!url) return false;
  return (
    url.includes("/api/auth/login") ||
    url.includes("/api/auth/register") ||
    url.includes("/api/auth/session") ||
    url.includes("/api/auth/refresh")
  );
}

/**
 * Decide whether a refresh failure represents a TRUE session end (refresh
 * token is dead) vs a transient failure (network blip, server 5xx, CDN
 * misconfig). Exported for unit tests.
 *
 * Returns `true` only when:
 *   - the response is a 401 AND the backend body explicitly mentions the
 *     refresh token being expired / invalid / missing / mistyped, OR
 *   - the local refresh attempt threw "no_refresh_token" because nothing
 *     was in localStorage to send.
 *
 * Returns `false` for: 5xx, network errors (no response), 401 from a
 * misconfigured proxy that doesn't include the backend detail, timeouts.
 */
export function shouldEndSessionOnRefreshFailure(refreshErr) {
  if (!refreshErr) return false;
  if (refreshErr.message === "no_refresh_token") return true;
  const status = refreshErr?.response?.status;
  if (status !== 401) return false;
  const detail = String(refreshErr?.response?.data?.detail || "").toLowerCase();
  return (
    detail.includes("refresh token") ||
    detail.includes("not a refresh")
  );
}

function detail401IndicatesExpired(err) {
  // Backend returns 401 with explicit messages; we treat any 401 from a
  // protected endpoint as "try refreshing", but expired/invalid messages
  // confirm the diagnosis.
  //
  // FastAPI standard error shape: { detail: "Token has expired" }
  // Some middlewares / proxies return: { message: "..." }
  // Be defensive across both. (Status code alone is never enough — we've
  // seen 200/403 responses with expired-token bodies in some auth proxies.)
  const data = err?.response?.data || {};
  const candidates = [data.detail, data.message, data.error, data.error_description];
  for (const c of candidates) {
    if (typeof c === "string") {
      const s = c.toLowerCase();
      if (
        s.includes("expired") ||
        s.includes("invalid token") ||
        s.includes("not authenticated") ||
        s.includes("invalid signature") ||
        s.includes("invalid bearer")
      ) return true;
    }
  }
  // Also check the raw response text in case a proxy stuffed the message
  // somewhere unstructured.
  const raw = typeof err?.response?.data === "string" ? err.response.data.toLowerCase() : "";
  if (raw.includes("token was expired") || raw.includes("token has expired")) return true;

  return true; // be permissive: try refresh on any 401, fall back gracefully.
}

// ─── Axios interceptors ────────────────────────────────────────────────

let installed = false;

/**
 * Boot-time proactive refresh. If the stored access token is within the
 * proactive-refresh window (or already expired) AND we have a refresh token,
 * fire a refresh BEFORE the first request goes out. This eliminates the
 * "first request after reopening the tab" race where the access token is
 * stale, the request gets a 401, and the response interceptor has to clean
 * up — slower and more visible to users than a quiet boot refresh.
 *
 * Best-effort: any failure here is silent. The normal request/response
 * interceptors still handle the same case reactively if this misses.
 */
function bootstrapRefresh() {
  // Run for whichever session matches the current page path (owner vs tech).
  try {
    const session = getActiveSession();
    if (!session.access || !session.refresh) return;
    if (!isExpiringSoon(session.access)) return;
    // Fire-and-forget — but use the same single-flight Promise so any
    // request that races us shares the result.
    refreshAccessToken().catch(() => { /* fall through; normal flow handles */ });
  } catch { /* defensive — never throw at boot */ }
}

/**
 * Foreground-resume refresh.
 *
 * Fired whenever the PWA / browser tab returns to the user:
 *   - document `visibilitychange` → state becomes "visible"
 *   - `pageshow` → bfcache (back/forward) restore on iOS Safari
 *   - window `focus` → tab regains focus after another window was on top
 *   - window `online` → connectivity restored after offline period
 *
 * If we have a session AND the access token is inside the FOREGROUND
 * window (or already expired) AND a refresh token exists, fire a refresh
 * BEFORE any request can race. Single-flight: if a refresh is already
 * in-flight, this is a no-op.
 *
 * Best-effort: every failure here is silent. If the refresh actually
 * fails (refresh token expired), the next API call's response
 * interceptor handles the hard logout exactly once via `endSession`.
 */
function refreshOnForegroundIfStale() {
  try {
    const session = getActiveSession();
    if (!session.access || !session.refresh) return;
    // Refresh if expiring soon OR already expired.
    if (!isExpiringSoon(session.access, FOREGROUND_REFRESH_WINDOW_MS)) return;
    refreshAccessToken().catch(() => { /* interceptor handles next 401 */ });
  } catch { /* defensive — never throw from a listener */ }
}

let foregroundListenersInstalled = false;
function installForegroundRefreshListeners() {
  if (foregroundListenersInstalled || typeof window === "undefined") return;
  foregroundListenersInstalled = true;

  const onVisibility = () => {
    if (document.visibilityState === "visible") refreshOnForegroundIfStale();
  };
  // `pageshow` with `persisted=true` fires when the page is restored from
  // the bfcache (iOS Safari does this aggressively when navigating back).
  const onPageShow = () => refreshOnForegroundIfStale();
  const onFocus = () => refreshOnForegroundIfStale();
  const onOnline = () => refreshOnForegroundIfStale();

  document.addEventListener("visibilitychange", onVisibility, { passive: true });
  window.addEventListener("pageshow", onPageShow, { passive: true });
  window.addEventListener("focus", onFocus, { passive: true });
  window.addEventListener("online", onOnline, { passive: true });
}

export function installAuthInterceptors() {
  if (installed) return;
  installed = true;

  // Boot-time refresh BEFORE any request can fire. Fire-and-forget; the
  // request interceptor's single-flight lock makes any concurrent request
  // wait on the same promise.
  bootstrapRefresh();

  // Re-check the token every time the app returns to foreground / online.
  // Eliminates the "logged out after the app sat in background for >1h"
  // class of bugs by proactively refreshing BEFORE the first network call
  // after resume can hit a stale access token.
  installForegroundRefreshListeners();

  // Request interceptor — pro-active refresh + Authorization header.
  axios.interceptors.request.use(
    async (config) => {
      // Skip auth endpoints entirely. They don't need bearer tokens and
      // refreshing here would create infinite recursion.
      if (isAuthEndpoint(config.url)) return config;

      const session = getActiveSession();
      const access = session.access;
      const refresh = session.refresh;
      // Pro-active refresh: token exists, paired refresh exists, and the
      // access token expires within the warning window.
      if (access && refresh && isExpiringSoon(access)) {
        try {
          const newPair = await refreshAccessToken();
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${newPair.token}`;
          return config;
        } catch {
          // Fall through and let the 401 path handle the hard logout.
        }
      }
      // Default: attach the current access token if no Authorization set.
      if (access && !config.headers?.Authorization) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${access}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor — reactive refresh on 401, retry once.
  axios.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error?.config;
      const status = error?.response?.status;

      // Handle session-ending failures from the refresh endpoint itself FIRST.
      // ONLY end the session when the response truly indicates the refresh
      // token is dead. CDNs/proxies can return 401 with no real backend
      // body; we don't want to forcibly logout a user with a perfectly
      // valid refresh token on misconfiguration.
      if (status === 401 && original?.url?.includes(REFRESH_PATH)) {
        if (shouldEndSessionOnRefreshFailure(error)) {
          endSession("refresh_failed");
        }
        // Otherwise let the original request's error bubble up; the next
        // request will trigger a fresh refresh attempt.
        return Promise.reject(error);
      }

      // Some auth middlewares (or stale CDN caches) return 200/403 with an
      // expired-token error body instead of 401. Match either signal so
      // we don't miss the refresh window. The body-string check is the
      // safety net the user explicitly asked for.
      const looksExpired = detail401IndicatesExpired(error);
      const bodyHints = (() => {
        try {
          const data = error?.response?.data;
          const txt = typeof data === "string" ? data : JSON.stringify(data || {});
          return /token (was|has) expired|jwt expired|expired token/i.test(txt);
        } catch { return false; }
      })();

      // Skip auth endpoints — no retry-with-refresh on login/register.
      const shouldTryRefresh =
        original &&
        !isAuthEndpoint(original.url) &&
        !original._authRetry &&
        (status === 401 || (bodyHints && (status === 403 || status === 200)));

      if (shouldTryRefresh) {
        if (!looksExpired && !bodyHints) return Promise.reject(error);

        original._authRetry = true; // ensure we only retry once per request.
        try {
          const newPair = await refreshAccessToken();
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newPair.token}`;
          return axios(original);
        } catch (refreshErr) {
          // CRITICAL: only end the session when the SERVER explicitly
          // rejected the refresh token, or when no refresh token exists
          // to begin with. Transient failures (network errors, 5xx,
          // timeouts, carrier handoffs, CDN 502) must NOT log the user
          // out — the next request will retry the refresh on its own
          // and the user stays logged in.
          //
          // This was the source of "random logouts during use on Android/
          // iPhone" — any brief 4G⇄WiFi handoff or 502 from a CDN edge
          // would fail one refresh call and forcibly end an otherwise
          // perfectly valid 90-day session.
          if (shouldEndSessionOnRefreshFailure(refreshErr)) {
            endSession("refresh_failed");
          }
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
}
